import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  SimpleGrid,
  Text,
  useToast,
} from '@chakra-ui/react';
import { MdDescription, MdInsertDriveFile } from 'react-icons/md';
import { AppCard, OutlineButton, UploadTile } from '../../../../components/ui';
import {
  deleteDocument,
  uploadDocument,
} from '../../../../repositories/document.repository';
import { setVillagerPhotoDocument } from '../../../../repositories/villager.repository';

/** Same identity files as villager mobile (signup + Documents). */
export const VILLAGER_DOC_SLOTS = [
  { docType: 'profile_photo', label: 'Profile photo', accept: 'image/*' },
  { docType: 'aadhaar_front', label: 'Aadhaar — Front', accept: 'image/*,application/pdf' },
  { docType: 'aadhaar_back', label: 'Aadhaar — Back', accept: 'image/*,application/pdf' },
  { docType: 'pan_card', label: 'PAN card', accept: 'image/*,application/pdf' },
  { docType: 'election_card', label: 'Election card', accept: 'image/*,application/pdf' },
  { docType: 'driving_license', label: 'Driving license', accept: 'image/*,application/pdf' },
];

const DOC_COLUMNS = { base: 1, sm: 2, md: 3 };

function toViewDoc(slot, raw) {
  if (!raw) return null;
  const mime = String(raw.mimeType || '').toLowerCase();
  const name = String(raw.fileName || '').toLowerCase();
  const isPdf = mime.includes('pdf') || name.endsWith('.pdf');
  return {
    id: raw.id,
    docType: slot.docType,
    label: slot.label,
    fileName: raw.fileName || 'attachment',
    mimeType: raw.mimeType || '',
    kind: isPdf ? 'pdf' : 'image',
    url: raw.url,
  };
}

function latestByType(docs) {
  const map = {};
  (docs || []).forEach((d) => {
    if (!d?.docType) return;
    if (!map[d.docType] || (d.id || 0) > (map[d.docType].id || 0)) {
      map[d.docType] = d;
    }
  });
  return map;
}

/**
 * Staff KYC files on villager detail — view, upload, or replace.
 */
export function VillagerDocumentsSection({ person, documents = [], onOpen, onChanged }) {
  const toast = useToast();
  const fileRef = useRef(null);
  const pendingTypeRef = useRef(null);
  const [busyType, setBusyType] = useState('');

  const byType = useMemo(() => latestByType(documents), [documents]);

  const pick = (slot) => {
    pendingTypeRef.current = slot.docType;
    if (fileRef.current) {
      fileRef.current.accept = slot.accept;
      fileRef.current.click();
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    const docType = pendingTypeRef.current;
    e.target.value = '';
    pendingTypeRef.current = null;
    if (!file || !docType || !person?.id) return;

    const slot = VILLAGER_DOC_SLOTS.find((s) => s.docType === docType);
    const existing = byType[docType];
    setBusyType(docType);
    try {
      const uploaded = await uploadDocument({
        ownerType: 'villager',
        ownerId: person.id,
        docType,
        file,
      });
      if (docType === 'profile_photo') {
        await setVillagerPhotoDocument(person.id, uploaded.id);
      }
      if (existing?.id && existing.id !== uploaded.id) {
        await deleteDocument(existing.id).catch(() => {});
      }
      toast({
        title: `${slot?.label || 'Document'} uploaded`,
        status: 'success',
        position: 'top-right',
      });
      await onChanged?.(docType === 'profile_photo' ? uploaded.id : undefined);
    } catch (err) {
      toast({
        title: err?.message || 'Upload failed',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setBusyType('');
    }
  };

  return (
    <AppCard mb={6}>
      <Heading size={{ base: 'sm', xl: 'md' }} mb={1}>
        Documents
      </Heading>
      <Text fontSize={{ base: '13px', xl: '14px' }} color="text.secondary" mb={4}>
        Same identity files as the villager mobile app. Staff can upload or replace if
        needed. Click a file to preview.
      </Text>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf"
        hidden
        onChange={handleFile}
      />
      <SimpleGrid columns={DOC_COLUMNS} spacing={{ base: 3, xl: 4 }}>
        {VILLAGER_DOC_SLOTS.map((slot) => {
          const doc = toViewDoc(slot, byType[slot.docType]);
          const busy = busyType === slot.docType;
          const isImage = doc?.kind === 'image' && !!doc?.url;
          const isPdf = doc?.kind === 'pdf';
          return (
            <Box
              key={slot.docType}
              overflow="hidden"
              bg="bg.input"
              borderRadius="sm"
              borderWidth="1px"
              borderColor="border.subtle"
            >
              <Text
                px={{ base: 3, xl: 4 }}
                pt={{ base: 3, xl: 4 }}
                fontSize={{ base: '13px', xl: '14px' }}
                fontWeight="600"
              >
                {slot.label}
              </Text>
              {doc?.url ? (
                <>
                  <Box
                    as="button"
                    type="button"
                    w="100%"
                    textAlign="left"
                    cursor="pointer"
                    onClick={() => onOpen?.(doc)}
                    sx={{ '& > *': { pointerEvents: 'none' } }}
                    _hover={{ opacity: 0.92 }}
                  >
                    {isImage ? (
                      <Box w="100%" h={{ base: '140px', xl: '160px' }} mt={2} overflow="hidden">
                        <Image
                          src={doc.url}
                          alt={slot.label}
                          w="100%"
                          h="100%"
                          objectFit="cover"
                        />
                      </Box>
                    ) : (
                      <Flex align="center" gap={3} px={{ base: 3, xl: 4 }} py={4}>
                        <Flex
                          w="44px"
                          h="44px"
                          align="center"
                          justify="center"
                          borderRadius="sm"
                          bg={isPdf ? 'whiteAlpha.100' : 'bg.amberTint'}
                          flexShrink={0}
                        >
                          <Icon
                            as={isPdf ? MdDescription : MdInsertDriveFile}
                            boxSize={6}
                            color="text.secondary"
                          />
                        </Flex>
                        <Text fontSize="13px" fontFamily="mono" noOfLines={1}>
                          {doc.fileName}
                        </Text>
                      </Flex>
                    )}
                  </Box>
                  <HStack px={{ base: 3, xl: 4 }} py={3} spacing={2}>
                    <OutlineButton
                      size="sm"
                      isLoading={busy}
                      onClick={() => pick(slot)}
                    >
                      Replace
                    </OutlineButton>
                    <OutlineButton size="sm" onClick={() => onOpen?.(doc)} isDisabled={busy}>
                      View
                    </OutlineButton>
                  </HStack>
                </>
              ) : (
                <Box px={{ base: 3, xl: 4 }} pt={3} pb={4}>
                  <UploadTile
                    label={busy ? 'Uploading…' : 'Upload file'}
                    onClick={() => !busy && pick(slot)}
                  />
                </Box>
              )}
            </Box>
          );
        })}
      </SimpleGrid>
    </AppCard>
  );
}
